import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, ChevronDown } from "lucide-react";
import { HERO_IMAGE } from "@/lib/site-data";
import BranchChooser from "./BranchChooser";

export default function Hero() {
    const [waOpen, setWaOpen] = useState(false);
    const [callOpen, setCallOpen] = useState(false);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100vh] flex items-end overflow-hidden"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMAGE}
                    alt="Authentic Thai massage at Aekkarag"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep via-maroon-deep/70 to-maroon-deep/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-maroon-deep/80 via-transparent to-transparent" />
            </div>

            {/* Decorative gold corner */}
            <div className="absolute top-24 right-6 lg:right-16 hidden md:block opacity-50">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                    <path
                        d="M2 2 L40 2 M2 2 L2 40 M118 2 L80 2 M118 2 L118 40"
                        stroke="#C9A961"
                        strokeWidth="1"
                    />
                </svg>
            </div>

            <div className="relative z-10 w-full max-w-7xl mx-auto px-5 lg:px-10 pb-24 lg:pb-32 pt-32">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className="max-w-3xl"
                >
                    <span className="eyebrow !text-gold-light flex items-center gap-3">
                        <span className="w-10 h-px bg-gold-light" />
                        Experience authentic Thai wellness
                    </span>

                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream tracking-tight leading-[1.05] mt-6">
                        Experience{" "}
                        <span className="text-gold-light">Authentic Thai Massage</span> &{" "}
                        <em className="text-gold not-italic font-light italic">True Relaxation</em>
                    </h1>

                    <p className="mt-7 text-lg lg:text-xl text-cream/80 max-w-xl leading-relaxed font-light">
                        Skilled Thai therapists. A calm, private space in Cape Town. Relax your
                        body, calm your mind — the way it has been done for centuries.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <button
                            onClick={() => setWaOpen(true)}
                            data-testid="hero-whatsapp-btn"
                            className="btn-gold"
                        >
                            <MessageCircle size={18} />
                            Book via WhatsApp
                        </button>
                        <button
                            onClick={() => setCallOpen(true)}
                            data-testid="hero-call-btn"
                            className="btn-outline-gold"
                        >
                            <Phone size={18} />
                            Call Now
                        </button>
                    </div>

                    <div className="mt-12 flex items-center gap-8 text-cream/60 text-xs tracking-widest uppercase">
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                            4,700+ happy customers
                        </span>
                        <span className="hidden sm:inline">·</span>
                        <span className="hidden sm:inline">Two Cape Town branches</span>
                    </div>
                </motion.div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 1 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/50 text-[0.65rem] tracking-[0.3em] uppercase flex flex-col items-center gap-2"
                >
                    Scroll
                    <ChevronDown size={14} className="animate-bounce" />
                </motion.div>
            </div>

            <BranchChooser
                open={waOpen}
                onOpenChange={setWaOpen}
                title="Book via WhatsApp"
                description="Choose your preferred branch — we'll open a WhatsApp chat with our team."
                message="Hi, I would like to book a Thai massage at Aekkarag. Could you share availability?"
            />
            <BranchChooser
                open={callOpen}
                onOpenChange={setCallOpen}
                title="Call us"
                description="Choose a branch to call directly."
            />
        </section>
    );
}
